import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import type { VistaSaldoCliente } from '@/types/database'
import { formatCurrency } from '@/lib/utils'

interface ClientesDeudoresProps {
  clientes: VistaSaldoCliente[]
  isLoading?: boolean
}

function getInitials(nombre: string): string {
  return nombre
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function ClientesDeudores({ clientes, isLoading }: ClientesDeudoresProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Clientes con Mayor Deuda</CardTitle>
          <Link to="/reportes" className="text-sm text-muted-foreground hover:text-foreground">
            Ver deudas
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="h-10 w-10 bg-muted animate-pulse rounded-full" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                  <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                </div>
                <div className="h-5 w-20 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Clientes con Mayor Deuda</CardTitle>
        <Link to="/reportes" className="text-sm text-muted-foreground hover:text-foreground">
          Ver deudas
        </Link>
      </CardHeader>
      <CardContent>
        {clientes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay clientes con deuda pendiente
          </p>
        ) : (
          <div className="space-y-4">
            {clientes.map((cliente, index) => (
              <div key={cliente.id} className="flex items-center gap-4">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className={index === 0 ? 'bg-red-100 text-red-600' : ''}>
                    {getInitials(cliente.nombre ?? '')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{cliente.nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    {cliente.responsable || 'Sin responsable'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-red-600">
                    {formatCurrency(cliente.saldo || 0)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
